import { getPriceString } from "components/Price/Price.utils";
import { useCurrencySymbol, useLocale } from "hooks";
import { useUser } from "lib/user";
import React, { useCallback, useEffect, useState } from "react";
import type { VariantTypeV2 } from "@shared/graphql.types";
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider";
import type { CMSPageModule } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils";
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils";
import type { PurchaseType } from "components/Shop/PDPPage/types";
import { dispatch } from "@shared/utils.events";
import { navigateTo } from "@shared/utils.routing";
import { useVariantIds } from "hooks/useVariantIds";
import type { AddBasketItemType } from "lib/api/addBasketItems";
import { addBasketItemsWithSpinner } from "lib/api/addBasketItems";
import { useExpressCheckoutContext } from "components/Basket/hooks/useExpressCheckoutContext";
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";
import {
  addedCtaStep,
  atbQuickShopCta,
  memberFontSize,
  nonMemberFontSize,
  quickShopCta
} from "./QuickShop.css";

interface Props {
  variant: VariantTypeV2;
  buttonVariant: string;
  pdpPurchaseType?: PurchaseType;
  setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>;
  setConfirmationVariant?: React.Dispatch<
    React.SetStateAction<VariantTypeV2 | undefined>
  >;
  source?: string;
  title?: string | undefined;
  locationType?: string;
  isATBConfirmation?: boolean;
  locationTypeDetails?: string;
  className?: string;
  handleAddToBag?: (
    variant: VariantTypeV2,
    locationType?: string
  ) => Promise<void>;
  trackAddToBagClick?: () => void;
}

export const useButtonText = (title, variant): string => {
  const user = useUser();

  const currencySym = useCurrencySymbol();
  const { pricing } = variant;

  const price = user.hasMembership ? pricing.members : pricing.guest;

  const priceString = getPriceString(price, currencySym);

  const buttonText = title ?? `Shop for ${priceString}`;

  return buttonText;
};

export const QuickShop: React.FC<Props> = ({
  variant,
  buttonVariant,
  pdpPurchaseType = "member",
  setConfirmation,
  isATBConfirmation = false,
  setConfirmationVariant,
  source = "",
  title = undefined,
  className,
  locationType,
  locationTypeDetails,
  handleAddToBag,
  trackAddToBagClick
}) => {
  const locale = useLocale();
  const { pageId, quickShopTracking } = useLandingPageContext();
  const isLandingPage = !!pageId;
  const [added, setAdded] = useState(false);
  const { handleExpressCheckout } = useExpressCheckoutContext();

  const { giftMembershipProductId } = useVariantIds();
  const isGiftMembership = variant.id === giftMembershipProductId;

  const buttonText = useButtonText(title, variant);

  const [buttonTitle, setButtonTitle] = useState(buttonText);

  const addedCta = added && buttonVariant === "atbMobile";

  const userHasMembershipCtaStyle = false;

  useEffect(() => {
    setButtonTitle(buttonText);
  }, [buttonText]);

  useEffect(() => {
    if (added) {
      setButtonTitle("Added!");
      setTimeout(() => {
        setButtonTitle(buttonText);
        setAdded(false);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [added]);

  const onClickHandler = async () => {
    if (handleAddToBag) {
      return handleAddToBag(variant, locationType);
    }

    if (isLandingPage) {
      cmsPageClick({
        ...quickShopTracking,
        moduleContentDetails: variant.sku
      } as CMSPageModule);

      return addToBag();
    }

    return addToBag();
  };

  const addToBag = useCallback(async () => {
    if (isGiftMembership) {
      await addBasketItemsWithSpinner(
        [
          {
            quantity: 1,
            variantId: giftMembershipProductId,
            variantSku:
              locale === "en-US" ? "BP_PLUS_GIFT_US" : "BP_PLUS_GIFT_UK",
            type: "product",
            origin: "user"
          }
        ],
        locationType,
        locationTypeDetails
      ).then(() => navigateTo("/basket"));
    } else {
      const items: AddBasketItemType[] = [
        {
          variantId: variant.id,
          quantity: 1,
          type: "product",
          origin: "user",
          variantSku: variant.sku
        }
      ];

      await addBasketItemsWithSpinner(items, locationType, locationTypeDetails)
        .then(() => {
          if (setConfirmation && pdpPurchaseType && setConfirmationVariant) {
            setConfirmationVariant(variant);
            setConfirmation(true);
          }
          setAdded(true);
        })
        .catch(() => setAdded(false));
      if (handleExpressCheckout) {
        handleExpressCheckout();
      }
    }
  }, [
    isGiftMembership,
    giftMembershipProductId,
    locale,
    locationType,
    locationTypeDetails,
    pdpPurchaseType,
    variant,
    setConfirmation,
    setConfirmationVariant,
    handleExpressCheckout
  ]);

  const trackAddToBagCta = (e) => {
    if (!e) return;
    e.stopPropagation();
    e.preventDefault();

    if (source) {
      dispatch("page element clicked", {
        target: source
      });
    }

    if (trackAddToBagClick) {
      trackAddToBagClick();
    }

    onClickHandler();
  };

  return (
    <DeprecatedUISharedButton
      className={`${className} ${quickShopCta} ${
        isATBConfirmation && atbQuickShopCta
      } ${addedCta && addedCtaStep} ${
        userHasMembershipCtaStyle ? nonMemberFontSize : memberFontSize
      }`}
      variant={"tertiary"}
      disabled={variant.availability.status === "outofstock"}
      handleClick={(e) => {
        trackAddToBagCta(e);
      }}
    >
      {added ? "Added!" : buttonTitle}
    </DeprecatedUISharedButton>
  );
};
